/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowLeftIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { FloatingPixelBackground, PixelLogo, PixelCorner } from '../components/pixel/PixelComponents';
import { isDisposableEmail } from '../utils/disposableEmailValidator';
import { authService } from '../services/api';
import { AuthConfig } from '../types';
import { isAllowedReturn, finishLogin } from '../utils/ssoReturn';

// Lazy import to avoid circular dependencies
const AccountFlaggedModal = React.lazy(() => import('../components/common/AccountFlaggedModal'));

// reCAPTCHA v3 Site Key
const RECAPTCHA_SITE_KEY = '6LcqAVwsAAAAAEFyVDvBkvgTO2IufYJspOKS7A4A';

// Google Client ID (feature flag: only show Google button when set)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// GitHub Client ID (feature flag: only show GitHub button when set)
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || '';

// Microsoft Client ID (feature flag: only show Microsoft button when set and enabled)
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';
const IS_MICROSOFT_LOGIN_ENABLED = process.env.REACT_APP_IS_MICROSOFT_LOGIN === 'true';

// Disable reCAPTCHA in development mode
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';
const RECAPTCHA_ENABLED = !IS_DEVELOPMENT;

// Declare grecaptcha type for TypeScript
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const GitHubIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
  </svg>
);

const MicrosoftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { requestOTP, verifyOTP, googleLogin, githubLogin, microsoftLogin, isAuthenticated } = useAuthStore();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [countdown, setCountdown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const recaptchaLoaded = useRef(false);

  // Auth methods, driven by backend truth (GET /public/auth-config) so the
  // login page renders only the methods actually enabled server-side.
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [ssoMode, setSsoMode] = useState(false);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);

  useEffect(() => {
    authService.getAuthConfig()
      .then((res) => setAuthConfig(res.data))
      // Email OTP is disabled by default (bot abuse). On fetch error, fail closed
      // for email_otp and still surface OAuth buttons from client-side env config.
      .catch(() => setAuthConfig({
        email_otp: false,
        google: !!GOOGLE_CLIENT_ID,
        github: !!GITHUB_CLIENT_ID,
        microsoft: !!MICROSOFT_CLIENT_ID && IS_MICROSOFT_LOGIN_ENABLED,
        enterprise_sso: false,
      }));
  }, []);

  // Load reCAPTCHA v3 script (only in production)
  useEffect(() => {
    if (!RECAPTCHA_ENABLED || recaptchaLoaded.current) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    recaptchaLoaded.current = true;

    return () => {
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  // SSO: stash the ?return= param (if allowlisted) so it survives the OAuth
  // round-trip - providers redirect back here without our query params.
  useEffect(() => {
    const ret = searchParams.get('return');
    if (ret && isAllowedReturn(ret)) {
      sessionStorage.setItem('sso_return', ret);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSO: if the user is ALREADY signed in on this domain, hand the existing JWT
  // straight back to the requesting portal (inference/training) instead of
  // showing the login form and stranding them on /portal. Fires once auth
  // resolves; the ref guards against a double hand-off after a fresh login.
  const ssoHandledRef = useRef(false);
  useEffect(() => {
    if (ssoHandledRef.current || !isAuthenticated) return;
    const ret = sessionStorage.getItem('sso_return');
    if (ret && isAllowedReturn(ret)) {
      ssoHandledRef.current = true;
      finishLogin(navigate);
    }
  }, [isAuthenticated, navigate]);

  // Handle OAuth callback - check for ?code= on mount (Google, GitHub, or Microsoft)
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code) return;

    // Clean up the URL (remove ?code=&state= from address bar)
    window.history.replaceState({}, '', window.location.pathname);

    const redirectUri = window.location.origin + window.location.pathname;

    const handleOAuthError = (error: any) => {
      const errorMsg = error?.response?.data?.error || '';
      if (errorMsg.toLowerCase().includes('deactivat')) {
        setShowFlaggedModal(true);
      } else {
        const lower = errorMsg.toLowerCase();
        const isSensitive = ['disposable', 'blocked', 'banned', 'spam', 'unsafe'].some((kw) => lower.includes(kw));
        toast.error(isSensitive ? 'Unable to sign in. Please try again.' : (errorMsg || 'Sign in failed. Please try again.'));
      }
    };

    if (state === 'google' && GOOGLE_CLIENT_ID) {
      const handleGoogleCallback = async () => {
        setIsSubmitting(true);
        try {
          await googleLogin(code, redirectUri);
          toast.success('Welcome!');
          finishLogin(navigate);
        } catch (error) {
          handleOAuthError(error);
        } finally {
          setIsSubmitting(false);
        }
      };
      handleGoogleCallback();
    } else if (state === 'microsoft' && MICROSOFT_CLIENT_ID && IS_MICROSOFT_LOGIN_ENABLED) {
      const handleMicrosoftCallback = async () => {
        setIsSubmitting(true);
        try {
          await microsoftLogin(code, redirectUri);
          toast.success('Welcome!');
          finishLogin(navigate);
        } catch (error) {
          handleOAuthError(error);
        } finally {
          setIsSubmitting(false);
        }
      };
      handleMicrosoftCallback();
    } else if (GITHUB_CLIENT_ID) {
      const handleGitHubCallback = async () => {
        setIsSubmitting(true);
        try {
          await githubLogin(code);
          toast.success('Welcome!');
          finishLogin(navigate);
        } catch (error) {
          handleOAuthError(error);
        } finally {
          setIsSubmitting(false);
        }
      };
      handleGitHubCallback();
    }
  }, [searchParams, googleLogin, githubLogin, microsoftLogin, navigate]);

  // Redirect to Google OAuth
  const handleGoogleLogin = () => {
    const redirectUri = window.location.origin + window.location.pathname;
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=openid%20email%20profile&state=google&access_type=online`;
  };

  // Redirect to GitHub OAuth
  const handleGitHubLogin = () => {
    const redirectUri = window.location.origin + window.location.pathname;
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&scope=user:email&redirect_uri=${encodeURIComponent(redirectUri)}`;
  };

  // Redirect to Microsoft OAuth
  const handleMicrosoftLogin = () => {
    const redirectUri = window.location.origin + window.location.pathname;
    window.location.href = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${MICROSOFT_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&response_mode=query&scope=openid%20email%20profile%20User.Read&state=microsoft`;
  };

  // Get reCAPTCHA token (returns undefined in development)
  const getRecaptchaToken = useCallback(async (action: string): Promise<string | undefined> => {
    // Skip reCAPTCHA in development
    if (!RECAPTCHA_ENABLED) {
      return undefined;
    }

    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
          resolve(token);
        } catch (error) {
          reject(error);
        }
      });
    });
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    // Silently block disposable emails - show the same "OTP sent" response
    // to avoid revealing that the email was detected as disposable
    if (isDisposableEmail(email)) {
      setIsSubmitting(true);
      // Brief delay to mimic real API call
      await new Promise((r) => setTimeout(r, 600));
      setIsSubmitting(false);
      toast.success('OTP sent to your email');
      setStep('otp');
      setCountdown(60);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      return;
    }

    setIsSubmitting(true);
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('login');
      await requestOTP(email, recaptchaToken);
      toast.success('OTP sent to your email');
      setStep('otp');
      setCountdown(60);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (error: any) {
      const status = error.response?.status;
      // For client errors related to email rejection (4xx except 429),
      // show the same "OTP sent" message to avoid leaking detection info
      if (status && status >= 400 && status < 500 && status !== 429) {
        toast.success('OTP sent to your email');
        setStep('otp');
        setCountdown(60);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } else if (status === 429) {
        toast.error('Too many requests. Please try again later.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedOtp = value.slice(0, 6).split('');
      const newOtp = [...otp];
      pastedOtp.forEach((char, i) => {
        if (i + index < 6) {
          newOtp[i + index] = char;
        }
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + pastedOtp.length, 5);
      inputRefs.current[nextIndex]?.focus();
    } else {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const [showFlaggedModal, setShowFlaggedModal] = useState(false);

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter the complete OTP');
      return;
    }
    setIsSubmitting(true);
    try {
      await verifyOTP(email, otpString);
      toast.success('Welcome!');
      finishLogin(navigate);
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || '';

      // Account deactivated - show flagged modal
      if (errorMsg.toLowerCase().includes('deactivat')) {
        setShowFlaggedModal(true);
        setIsSubmitting(false);
        return;
      }

      // Sanitize: never reveal sensitive info in error messages
      const lower = errorMsg.toLowerCase();
      const isSensitive = ['disposable', 'blocked', 'banned', 'spam', 'unsafe'].some((kw) => lower.includes(kw));
      toast.error(isSensitive ? 'Unable to verify. Please try again.' : (errorMsg || 'Invalid or expired OTP'));
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('resend_otp');
      await requestOTP(email, recaptchaToken);
      toast.success('OTP resent to your email');
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch {
      // Always show "OTP resent" to avoid leaking email status
      toast.success('OTP resent to your email');
      setCountdown(60);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleBack = () => {
    setStep('email');
    setOtp(['', '', '', '', '', '']);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      <FloatingPixelBackground intensity="medium" />

      {/* Pixel corner decorations - hidden on very small screens */}
      <div className="hidden sm:block">
        <PixelCorner position="top-left" />
        <PixelCorner position="top-right" />
        <PixelCorner position="bottom-left" />
        <PixelCorner position="bottom-right" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-3 sm:gap-4">
            <div className="scale-75 sm:scale-100">
              <PixelLogo scale={1} animate={true} />
            </div>
            <span className="text-2xl sm:text-3xl font-bold text-primary-500">Podstack</span>
          </div>
          <p className="mt-2 text-sm sm:text-base text-gray-400">GPU Cloud Platform</p>
        </div>

        {/* Login Card */}
        <div className="bg-gray-900 rounded-2xl shadow-xl p-5 sm:p-8 border border-gray-800">
          {step === 'email' ? (
            <>
              {(() => {
                // While auth-config loads, keep a neutral title. Once loaded,
                // email OTP off → providers-only copy (no misleading email CTA).
                const emailEnabled = authConfig === null ? null : !!authConfig.email_otp;
                const title = emailEnabled === false ? 'Sign in' : 'Welcome';
                const subtitle =
                  emailEnabled === false
                    ? 'Sign in or create an account with a provider below'
                    : 'Enter your email to sign in or create an account';
                return (
                  <>
                    <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">
                      {title}
                    </h2>
                    <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6">
                      {authConfig === null ? 'Loading sign-in options…' : subtitle}
                    </p>
                  </>
                );
              })()}

              {authConfig === null ? (
                <div className="flex items-center justify-center py-10">
                  <svg className="animate-spin h-6 w-6 text-primary-500" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                </div>
              ) : (
                <>
                  {authConfig.email_otp && (
                    <form onSubmit={handleRequestOTP} className="space-y-5">
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-300 mb-1"
                        >
                          Email
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setEmailError(null);
                            }}
                            className={`input pl-10 min-h-[48px] text-base ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
                            placeholder="you@example.com"
                            required
                            autoFocus
                            aria-label="Email address"
                          />
                        </div>
                        {emailError && (
                          <div className="mt-2 flex items-start gap-2 text-red-400">
                            <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                            <p className="text-sm">{emailError}</p>
                          </div>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full h-12 text-base font-medium"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg
                              className="animate-spin h-5 w-5"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                              />
                            </svg>
                            Sending OTP...
                          </span>
                        ) : (
                          'Continue with Email'
                        )}
                      </button>
                    </form>
                  )}

                  {/* Social Sign-In - "or" only when email OTP is also shown */}
                  {((authConfig.google && GOOGLE_CLIENT_ID) || (authConfig.github && GITHUB_CLIENT_ID) || (authConfig.microsoft && MICROSOFT_CLIENT_ID && IS_MICROSOFT_LOGIN_ENABLED)) && (
                    <>
                      {authConfig.email_otp && (
                        <div className="flex items-center my-5">
                          <div className="flex-1 border-t border-gray-700" />
                          <span className="px-3 text-sm text-gray-500">or</span>
                          <div className="flex-1 border-t border-gray-700" />
                        </div>
                      )}
                      <div className="space-y-3">
                        {authConfig.google && GOOGLE_CLIENT_ID && (
                          <button
                            type="button"
                            onClick={handleGoogleLogin}
                            disabled={isSubmitting}
                            className="w-full h-12 flex items-center justify-center gap-3 px-4 bg-white hover:bg-gray-100 text-gray-800 font-medium text-base rounded-lg border border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <GoogleIcon className="w-5 h-5" />
                            Continue with Google
                          </button>
                        )}
                        {authConfig.github && GITHUB_CLIENT_ID && (
                          <button
                            type="button"
                            onClick={handleGitHubLogin}
                            disabled={isSubmitting}
                            className="w-full h-12 flex items-center justify-center gap-3 px-4 bg-white hover:bg-gray-100 text-gray-800 font-medium text-base rounded-lg border border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <GitHubIcon className="w-5 h-5" />
                            Continue with GitHub
                          </button>
                        )}
                        {authConfig.microsoft && MICROSOFT_CLIENT_ID && IS_MICROSOFT_LOGIN_ENABLED && (
                          <button
                            type="button"
                            onClick={handleMicrosoftLogin}
                            disabled={isSubmitting}
                            className="w-full h-12 flex items-center justify-center gap-3 px-4 bg-white hover:bg-gray-100 text-gray-800 font-medium text-base rounded-lg border border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <MicrosoftIcon className="w-5 h-5" />
                            Continue with Microsoft
                          </button>
                        )}
                      </div>
                    </>
                  )}

                  {/* Enterprise SSO */}
                  {authConfig.enterprise_sso && (
                    <div className="mt-4">
                      {!ssoMode ? (
                        <button
                          type="button"
                          onClick={() => setSsoMode(true)}
                          className="w-full h-12 flex items-center justify-center gap-3 px-4 bg-white hover:bg-gray-100 text-gray-800 font-medium text-base rounded-lg border border-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue with company SSO
                        </button>
                      ) : (
                        <form
                          onSubmit={async (e) => {
                            e.preventDefault();
                            setSsoLoading(true);
                            try {
                              const res = await authService.ssoLookup(ssoEmail);
                              window.location.href = res.data.authorize_url;
                            } catch {
                              toast.error('No SSO configuration found for this email domain');
                            } finally {
                              setSsoLoading(false);
                            }
                          }}
                          className="space-y-3"
                        >
                          <input
                            type="email"
                            required
                            value={ssoEmail}
                            onChange={(e) => setSsoEmail(e.target.value)}
                            placeholder="you@company.com"
                            className="input"
                            autoFocus
                          />
                          <button
                            type="submit"
                            disabled={ssoLoading}
                            className="btn-primary w-full h-12 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {ssoLoading ? 'Looking up…' : 'Continue'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setSsoMode(false)}
                            className="w-full text-sm text-gray-400 hover:text-primary-500 transition-colors"
                          >
                            Back
                          </button>
                        </form>
                      )}
                    </div>
                  )}

                  {/* No auth methods configured */}
                  {(() => {
                    const hasAnyMethod =
                      authConfig.email_otp ||
                      (authConfig.google && GOOGLE_CLIENT_ID) ||
                      (authConfig.github && GITHUB_CLIENT_ID) ||
                      (authConfig.microsoft && MICROSOFT_CLIENT_ID && IS_MICROSOFT_LOGIN_ENABLED) ||
                      authConfig.enterprise_sso;
                    return !hasAnyMethod ? (
                      <div className="mt-4 rounded-xl border border-gray-700 bg-gray-800/50 px-4 py-6 text-center">
                        <p className="text-sm text-gray-300">
                          No sign-in providers are configured for this environment.
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Set GOOGLE_CLIENT_ID, GITHUB_CLIENT_ID, or enable email OTP to enable login.
                        </p>
                      </div>
                    ) : null;
                  })()}
                </>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleBack}
                className="flex items-center text-gray-400 hover:text-primary-500 mb-4 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-1" />
                Back
              </button>

              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">
                Check your email
              </h2>
              <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6">
                We sent a 6-digit code to <span className="font-medium text-primary-500 break-all">{email}</span>
              </p>

              <form onSubmit={handleVerifyOTP} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2 sm:mb-3">
                    Enter verification code
                  </label>
                  <div className="flex gap-1.5 sm:gap-2 justify-center">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => { inputRefs.current[index] = el; }}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-10 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-semibold bg-gray-800 border border-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || otp.join('').length !== 6}
                  className="btn-primary w-full py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Verifying...
                    </span>
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>

                <div className="text-center">
                  <p className="text-gray-400 text-sm">
                    Didn't receive the code?{' '}
                    {countdown > 0 ? (
                      <span className="text-gray-500">
                        Resend in {countdown}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        className="text-primary-500 hover:text-primary-400 font-medium"
                      >
                        Resend OTP
                      </button>
                    )}
                  </p>
                </div>
              </form>
            </>
          )}
        </div>

        {/* View Products & Services */}
        <div className="mt-4 sm:mt-6 text-center">
          <a
            href="https://podstack.ai/#products"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full h-12 px-6 text-base font-medium text-primary-500 bg-transparent border-2 border-primary-500 rounded-lg hover:bg-primary-500 hover:text-white transition-colors"
          >
            View Products & Services
          </a>
        </div>

        {/* Footer */}
        <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-gray-500 px-2">
          By continuing, you agree to our{' '}
          <a
            href="https://podstack.ai/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 hover:underline"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <Link
            to="/privacy-policy"
            className="text-primary-500 hover:underline"
          >
            Privacy Policy
          </Link>
        </p>
        <p className="mt-2 text-center text-xs sm:text-sm text-gray-500">
          <Link
            to="/contact-us"
            className="text-primary-500 hover:underline"
          >
            Contact Us
          </Link>
          <span className="mx-2 text-gray-600">·</span>
          <a
            href="https://podstack.ai/refund-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 hover:underline"
          >
            Refund Policy
          </a>
          <span className="mx-2 text-gray-600">·</span>
          <a
            href="https://podstack.ai/refund-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-500 hover:underline"
          >
            Cancellation Policy
          </a>
        </p>
      </div>

      {/* Account flagged modal - shown when a deactivated user tries to verify OTP */}
      <React.Suspense fallback={null}>
        <AccountFlaggedModal
          isOpen={showFlaggedModal}
          onClose={() => {
            setShowFlaggedModal(false);
            setStep('email');
            setOtp(['', '', '', '', '', '']);
          }}
        />
      </React.Suspense>
    </div>
  );
};

export default Login;
